import type { DocNode } from '@atlaskit/adf-schema';
import { FlagsProvider } from '@atlaskit/flag';
import Image from '@atlaskit/image';
import { BlankObjectExperienceModal } from '@atlassian/conversation-assistant-blank-object-experience/blank-object-experience-modal';
import { getCloudId, useDatabaseContent } from '@databases/database-state';
import { bridgeEventEmitter, DatabaseStreamState } from '@databases/rovo-bridge';
import { container } from '@databases/service-container';
import React, { useCallback } from 'react';
import { IntlProvider } from 'react-intl';
import { defineMessages, useIntl } from 'react-intl-next';

import {
  BoeSubmitPromptCommand,
  OpenRovoSidePanelCommand,
} from '@canvas/databases-bridge-api/commands';
import { CONFLUENCE_AFM_CLIENT_ID } from '@canvas/databases-bridge-api/types';
import { client } from '@canvas/rovo-content-bridge-api';

import databaseCwrModalDark from '../../../assets/database_cwr_modal_dark.svg';
import databaseCwrModalLight from '../../../assets/database_cwr_modal_light.svg';

import { useDatabaseSmartPrompts } from './useDatabaseSmartPrompts';

const messages = defineMessages({
  headerText: {
    id: 'database-blank-object-experience-modal.header-text',
    defaultMessage: 'Let\u2019s create a database',
    description: 'Heading text shown in the blank object experience modal for databases',
  },
});

interface DatabaseBlankObjectExperienceModalProps {
  onClose: () => void;
}

export const DatabaseBlankObjectExperienceModal = ({
  onClose,
}: DatabaseBlankObjectExperienceModalProps) => {
  const { formatMessage, locale } = useIntl();
  const siteId = getCloudId() ?? '';
  const { contentId } = useDatabaseContent();
  const panelManager = container.resolve('PanelManager');
  const smartPrompts = useDatabaseSmartPrompts();

  const handleTemplateSelect = useCallback(() => {
    panelManager.showPanel('DatabaseSetupPanel', { initialTab: 'templates' });
    onClose();
  }, [panelManager, onClose]);

  const handleImport = useCallback(() => {
    panelManager.showPanel('DatabaseSetupPanel', { initialTab: 'import' });
    onClose();
  }, [panelManager, onClose]);

  const handleSubmit = useCallback(
    (prompt: DocNode) => {
      // Extract plain text from ADF DocNode by recursively walking the node tree
      let plainText = '';
      const walk = (node: any) => {
        if (node.type === 'text') {
          plainText += node.text ?? '';
        }
        if (node.content) {
          (node.content as any[]).forEach(walk);
        }
      };
      walk(prompt);

      onClose();

      // Subscribe once to stream completion and open the Rovo side panel when done.
      let hasSeenStreaming = false;
      const unsubscribe = DatabaseStreamState.getInstance().subscribe((state) => {
        if (state.isStreaming) {
          hasSeenStreaming = true;
        } else if (hasSeenStreaming) {
          unsubscribe();
          bridgeEventEmitter.sendEvent(OpenRovoSidePanelCommand, {
            senderId: client(`confluence-database-${contentId}`),
            receiverId: CONFLUENCE_AFM_CLIENT_ID,
          });
        }
      });

      bridgeEventEmitter.sendEvent(BoeSubmitPromptCommand, {
        senderId: client(`confluence-database-${contentId}`),
        receiverId: CONFLUENCE_AFM_CLIENT_ID,
        prompt: plainText.trim(),
        contentId,
      });
    },
    [onClose, contentId]
  );

  return (
    <IntlProvider locale={locale}>
      <FlagsProvider>
        <BlankObjectExperienceModal
          headerText={formatMessage(messages.headerText)}
          illustration={
            <Image src={databaseCwrModalLight} srcDark={databaseCwrModalDark} alt="" />
          }
          onClose={onClose}
          onTemplateSelect={handleTemplateSelect}
          onImport={handleImport}
          blankObjectExperienceProps={{
            siteId,
            smartPrompts,
            onSubmit: handleSubmit,
          }}
        />
      </FlagsProvider>
    </IntlProvider>
  );
};
